import styled, { css } from 'styled-components';

import { media, pxToRem } from '@yoweb/ui/styles/utils';
import {
  getRadii,
  getColor,
  getZIndex,
  getFontSize,
  getLineHeight,
  getLetterSpacing,
  getSpace,
  getFontWeight,
} from '@yoweb/ui/styles/utils/theme';
import { Text } from '@yoweb/ui/components/typography';

export const DeleteButton = styled.button`
  background: transparent;
  pointer-events: none;
  border: 0;
  cursor: pointer;
  display: none;
  justify-content: center;
  padding: 0;
  text-align: left;
  height: 16px;
  width: 16px;
  -webkit-tap-highlight-color: transparent;
  opacity: 0;

  :focus {
    opacity: 1;
    pointer-events: all;
  }

  ${media.lg`
    display: flex;
  `}
`;

type FormTextVariant = {
  textVariant?: 'display';
};

type LabelProps = {
  isDisabled: boolean;
  elevatedLabel: boolean;
} & FormTextVariant;

export const Label = styled(Text)<LabelProps>`
  && {
    color: ${getColor('base700')};
    position: absolute;
    top: ${getSpace('normal1')};
    left: ${getSpace('normal1')};
    pointer-events: none;
    transform: translate3d(0, 0, 0);
    transition: all 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    z-index: ${getZIndex('layer')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: calc(100% - 2rem);

    ${({ textVariant, elevatedLabel }) =>
      textVariant === 'display' &&
      !elevatedLabel &&
      css`
        font-size: ${getFontSize('titleXs')};
        font-weight: ${getFontWeight('bold')};
      `}

    ${({ elevatedLabel }) =>
      elevatedLabel &&
      css`
        transform: translate3d(0, -${pxToRem(8)}, 0);
      `}

    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: ${getColor('baseA300')};
      `};
  }
`;

export const Required = styled.span`
  position: relative;
  right: -${pxToRem(3)};
  color: ${getColor('critical')};
`;

export const formStylesCss = {
  font: css`
    color: ${getColor('base900')};
    font-size: ${getFontSize('bodyLg')};
    letter-spacing: ${getLetterSpacing('bodyLg')};
    line-height: ${getLineHeight('bodyLg')};

    ${({ textVariant }: FormTextVariant) =>
      textVariant === 'display' &&
      css`
        font-size: ${getFontSize('titleXs')};
        font-weight: ${getFontWeight('bold')};
      `}
  `,
  base: css`
    appearance: none;
    background-color: ${getColor('base000')};
    border: none;
    border-radius: ${getRadii('small')};
    box-sizing: border-box;
    outline: none;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  `,
  baseHover: css`
    &:hover {
      background-color: ${getColor('base050')};
      box-shadow: ${getColor('base900')} 0 0 0 1px;
    }
  `,
  inputSpacing: css`
    ${({ elevatedLabel }: Pick<FormInputCommonProps, 'elevatedLabel'>) => {
      if (elevatedLabel) {
        return css`
          padding-bottom: ${pxToRem(7)};
          padding-top: ${pxToRem(25)};
        `;
      }

      return css`
        padding-bottom: ${pxToRem(16)};
        padding-top: ${pxToRem(16)};
      `;
    }}
  `,
  disabled: css`
    background-color: ${getColor('base050')};
    box-shadow: none;
    color: ${getColor('baseA300')};
  `,
  focus: css`
    box-shadow: ${getColor('base900')} 0 0 0 2px;

    /* stylelint-disable-next-line selector-no-vendor-prefix */
    &::-webkit-input-placeholder {
      /* WebKit browsers */
      color: ${getColor('base700')};
    }

    /* stylelint-disable-next-line selector-no-vendor-prefix */
    &::-moz-placeholder {
      /* Mozilla Firefox 19+ */
      color: ${getColor('base700')};
    }

    &::placeholder {
      color: ${getColor('base700')};
    }
  `,
  withError: css`
    box-shadow: ${getColor('critical')} 0 0 0 1px;
  `,
  withErrorFocus: css`
    box-shadow: ${getColor('critical')} 0 0 0 2px;
  `,
  withBorder: css`
    box-shadow: ${getColor('base400')} 0 0 0 1px;
  `,
};

export type FormInputCommonProps = {
  hasError: boolean;
  elevatedLabel?: boolean;
} & FormTextVariant;

export const formInputCommon = css<FormInputCommonProps>`
  ${formStylesCss.base};
  ${formStylesCss.baseHover};
  ${formStylesCss.font};
  ${formStylesCss.inputSpacing};

  &:disabled {
    ${formStylesCss.disabled};
  }

  &:focus {
    ${formStylesCss.focus};
  }

  &:focus-within {
    ${formStylesCss.focus};
  }

  ${formStylesCss.withBorder};

  ${({ hasError }) =>
    hasError &&
    css`
      ${formStylesCss.withError};

      &:focus {
        ${formStylesCss.withErrorFocus};
      }
    `}
`;

export const Caption = styled.div`
  display: grid;
  grid-gap: ${getSpace('small3')};
  padding-top: ${getSpace('small3')};
`;

export const FormMessage = styled.div`
  display: grid;
  font-size: ${getFontSize('interactiveSm')};
  grid-template-columns: auto 1fr;
  grid-gap: ${getSpace('small2')};
  letter-spacing: ${getLetterSpacing('interactiveSm')};
  line-height: ${getLineHeight('interactiveSm')};
`;
